<script setup lang="ts">
import { ref, onUnmounted } from 'vue'
import { useRecentStore } from '../../stores/recentStore'
import type { RecentItemDto } from '../../dtos'

const emit = defineEmits<{
  (e: 'navigate-to-song', bandId: number, songId: number): void
  (e: 'navigate-to-mix', bandId: number, songId: number, mixId: number): void
}>()

const recentStore = useRecentStore()
const expanded = ref(false)
const rootEl = ref<HTMLElement | null>(null)

async function toggle() {
  expanded.value = !expanded.value
  // Always refetch on open — list changes whenever the user makes any edit,
  // and the endpoint is cheap (single indexed query + a small join).
  if (expanded.value) await recentStore.fetchRecent(50)
}

function clickItem(item: RecentItemDto) {
  expanded.value = false
  if (item.kind === 'song') {
    emit('navigate-to-song', item.bandId, item.id)
  } else if (item.kind === 'mix' && item.songId != null) {
    emit('navigate-to-mix', item.bandId, item.songId, item.id)
  }
}

// Close when clicking outside the dropdown.
function onDocumentClick(e: MouseEvent) {
  if (!expanded.value || !rootEl.value) return
  if (!rootEl.value.contains(e.target as Node)) expanded.value = false
}
document.addEventListener('mousedown', onDocumentClick)
onUnmounted(() => document.removeEventListener('mousedown', onDocumentClick))

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 60_000) return 'just now'
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`
  return new Date(iso).toLocaleDateString()
}
</script>

<template>
  <div ref="rootEl" class="relative bg-slate-100 border-b border-slate-200 w-[260px] mx-auto" :class="{ 'rounded-b-lg overflow-hidden': !expanded }">
    <button
      class="w-full flex items-center justify-between px-3 py-1.5 hover:bg-slate-50 transition-colors"
      @click="toggle"
    >
      <span class="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Recent items</span>
      <i class="pi text-slate-400" :class="expanded ? 'pi-chevron-up' : 'pi-chevron-down'" style="font-size: 0.7rem"></i>
    </button>
    <div v-if="expanded" class="absolute top-full left-0 w-[260px] bg-slate-100 border border-slate-200 rounded-b-lg shadow-lg max-h-72 overflow-y-auto z-50">
      <div v-if="recentStore.loading" class="px-3 py-4 text-center text-xs text-slate-400">
        <i class="pi pi-spin pi-spinner mr-1"></i> Loading...
      </div>
      <div v-else-if="recentStore.items.length === 0" class="px-3 py-4 text-center text-xs text-slate-400 italic">
        No recent items yet.
      </div>
      <ul v-else class="divide-y divide-slate-300">
        <li v-for="item in recentStore.items" :key="`${item.kind}-${item.id}`">
          <button
            class="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left transition-colors"
            @click="clickItem(item)"
          >
            <i
              class="pi text-slate-400 shrink-0"
              :class="item.kind === 'mix' ? 'pi-sliders-h' : 'pi-volume-up'"
              style="font-size: 0.85rem"
            ></i>
            <div class="flex-1 min-w-0">
              <div class="text-sm text-slate-900 truncate">{{ item.title }}</div>
              <div v-if="item.kind === 'mix' && item.parentTitle" class="text-[10px] text-slate-400 truncate">in {{ item.parentTitle }}</div>
            </div>
            <span class="text-[10px] text-slate-400 shrink-0">{{ relativeTime(item.touchedAt) }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
